import * as React from "react";

import { Redirect } from "react-router-dom";
import { RouterViewTemplate, useLoadingState } from "../RouterView";
import { CashbackLimit, CashbackLimitSettings, CashbackTypeV2 } from "../../http/protocol";
import { SavedCashbackLimit, SavedCashbackLimitSettings } from "../../services/CashbackLimitService";
import { Form, useForm } from "../form/Form";
import { getContainer } from "../../ioc/IOCSetup";
import { FormGroup } from "../form/FormGroup";
import { FormInput } from "../form/FormInput";
import { FormSubmitError, FormSubmitSuccess } from "../form/FormFeedback";
import { Submit } from "../form/Submit";
import { Validators, Validator } from "../form/Validators";
import { FeatureFlags, useFeatureFlag } from "../../feature-flags/FeatureFlagContext";
import { t } from "../../i18n";

// Stable section order so each type maps to a fixed index in the form model's limits array.
const LIMIT_TYPES: CashbackTypeV2[] = [CashbackTypeV2.CASHBACK, CashbackTypeV2.RAKEBACK];

function typeLabel(type: CashbackTypeV2): string {
  switch (type) {
    case CashbackTypeV2.CASHBACK:
      return t("cashback.limits.type-cashback");
    case CashbackTypeV2.RAKEBACK:
      return t("cashback.limits.type-rakeback");
  }
}

// Cross-field guard mirroring the server's min <= max rule, so the operator sees the constraint
// inline instead of only after a rejected save. Both bounds are optional, so only enforce when set.
function maxAtLeastMin(index: number): Validator<number> {
  return {
    validate(value: number, model: CashbackLimitSettings): boolean {
      const min = model?.limits?.[index]?.min;
      if (value == null || "" + value === "" || min == null || "" + min === "") {
        return true;
      }
      return Number(value) >= Number(min);
    },
    getName: () => "range",
  };
}

// A number field is blank when unset or cleared to an empty string; the form model carries
// both shapes, so both count as "no value entered".
function isBlank(value: unknown): boolean {
  return value == null || "" + value === "";
}

// Optional numeric bound: a blank field is valid ("no limit") and never blocks the form; a set
// value must be non-negative. Blank stays valid because the server treats an absent bound as
// null, whereas Validators.min(0) would reject a blank field (undefined >= 0 is false).
function nonNegativeWhenSet(): Validator<number> {
  return {
    validate(value: number): boolean {
      return isBlank(value) || Number(value) >= 0;
    },
    getName: () => "range",
  };
}

// Match the stored scale (DECIMAL(19,4)) so a near-100 percent the server would round to 100 is
// caught here rather than accepted and rejected server-side.
function round4(value: unknown): number {
  return Math.round(Number(value) * 10000) / 10000;
}

function percentWhenSet(): Validator<number> {
  return {
    validate(value: number): boolean {
      if (isBlank(value)) {
        return true;
      }
      const percent = round4(value);
      return percent >= 0 && percent < 100;
    },
    getName: () => "range",
  };
}

// Blank/absent saves as 0 ("no floor"); otherwise the entered percent, rounded to the stored scale.
function normalizeRtpFloor(settings: CashbackLimitSettings): number {
  const percent = settings.minEligibleRtp;
  return isBlank(percent) ? 0 : round4(percent);
}

// Illustrative basis for the worked example shown beside the RTP floor field.
const EXAMPLE_WAGER = 100;
const EXAMPLE_RAKEBACK_RATE = 0.1;
const EXAMPLE_SAMPLE_RTPS = [0.965, 0.92, 0.8];

// The usable floor as a fraction for the worked example, or null when the entry is not a usable
// floor (blank, or rounds to 0 or >= 100). The example math is fraction-space, so scale by 100.
function activeRtpFloor(value: unknown): number | null {
  if (isBlank(value)) {
    return null;
  }
  const percent = round4(value);
  if (isNaN(percent) || percent <= 0 || percent >= 100) {
    return null;
  }
  return percent / 100;
}

// Rakeback on the illustrative wager for a game whose true RTP is `rtp`.
function examplePayout(rtp: number): number {
  return EXAMPLE_WAGER * EXAMPLE_RAKEBACK_RATE * (1 - rtp);
}

// A game below the floor is treated as if its RTP were the floor, so its effective house edge
// is capped at 1 - floor.
function examplePayoutWithFloor(rtp: number, floor: number): number {
  const cappedEdge = Math.min(1 - rtp, 1 - floor);
  return EXAMPLE_WAGER * EXAMPLE_RAKEBACK_RATE * cappedEdge;
}

function formatEuro(amount: number): string {
  return amount.toFixed(2);
}

// Percentages read cleanest without trailing zeros: 0.965 -> "96.5", 0.8 -> "80".
function formatPercent(fraction: number): string {
  return (fraction * 100).toFixed(2).replace(/\.?0+$/, "");
}

// The settings document may omit a type or arrive in any order; project it onto LIMIT_TYPES
// so the rendered sections and their model indices stay deterministic.
function normalizeLimits(settings: CashbackLimitSettings): CashbackLimit[] {
  return LIMIT_TYPES.map((type) => {
    const existing = settings.limits?.find((limit) => limit.type === type);
    return existing ?? { type, enabled: false };
  });
}

// A blank min/max is sent as an explicit null so the server records "no limit" rather than a
// literal 0, which would cap every payout at zero.
function toSavedLimit(limit: CashbackLimit): SavedCashbackLimit {
  return {
    ...limit,
    min: isBlank(limit.min) ? null : Number(limit.min),
    max: isBlank(limit.max) ? null : Number(limit.max),
  };
}

export const EditCashbackLimits = (): JSX.Element => {
  const cashbackV2Enabled = useFeatureFlag(FeatureFlags.CASHBACK_V2_ENABLED);
  const service = getContainer().getCashbackLimitService();

  const [form, formListener, setForm] = useForm<CashbackLimitSettings>({ limits: normalizeLimits({}) }, (): Promise<void> => {
    const toSave: SavedCashbackLimitSettings = {
      ...form.model,
      minEligibleRtp: normalizeRtpFloor(form.model),
      limits: normalizeLimits(form.model).map(toSavedLimit),
    };
    return service.saveSettings(toSave).then((saved: CashbackLimitSettings) => {
      setForm({ model: { ...saved, limits: normalizeLimits(saved) }, validationMap: form.validationMap });
    });
  });

  const status = useLoadingState(async () => {
    // The page is flag-gated and redirects when off; skip the fetch so a disabled flag costs no call.
    if (!cashbackV2Enabled) {
      return;
    }
    const settings = await service.getSettings();
    setForm({ model: { ...settings, limits: normalizeLimits(settings) }, validationMap: {} });
  }, [cashbackV2Enabled]);

  if (!cashbackV2Enabled) {
    return <Redirect to="/cashback/configs/list" />;
  }

  const renderGlobalSettings = (): JSX.Element => (
    <div className="card-box">
      <div className="ibox-content">
        <h4 className="ibox-title">{t("cashback.limits.global-settings-title")}</h4>
        <div className="hr-line-dashed"></div>

        <FormGroup className="form-group">
          <label className="col-sm-3 control-label">{t("cashback.limits.max-hold-days-label")}</label>
          <div className="col-sm-10">
            <FormInput
              model="maxHoldDays"
              validators={[Validators.required(), Validators.integer(), Validators.min(1)]}
              type="integer"
              className="form-control"
              data-testid="cashback-limits-max-hold-days"
            />
            <span className="help">{t("cashback.limits.max-hold-days-help")}</span>
          </div>
        </FormGroup>

        <FormGroup className="form-group">
          <label className="col-sm-3 control-label">{t("cashback.limits.min-eligible-rtp-label", undefined, "Lowest Eligible Game RTP (%)")}</label>
          <div className="col-sm-10">
            <FormInput
              model="minEligibleRtp"
              validators={[Validators.number(), percentWhenSet()]}
              type="float"
              className="form-control"
              data-testid="cashback-limits-min-eligible-rtp"
            />
            <span className="help">
              {t(
                "cashback.limits.min-eligible-rtp-help",
                undefined,
                "The lowest RTP a game is treated as, entered as a percentage (e.g. 96.5 = 96.5% RTP), when calculating RTP-based cashback and rakeback. A lower-RTP game is treated as if it had this RTP. Leave empty or 0 for no floor.",
              )}
            </span>
            {renderRtpExample()}
          </div>
        </FormGroup>
      </div>
    </div>
  );

  const renderRtpExample = (): JSX.Element => {
    const raw = form.model.minEligibleRtp;
    const floor = activeRtpFloor(raw);
    // Out of range = set but not a usable floor (rounds to >= 100, negative, or non-numeric); blank
    // or rounding to 0 is "no floor". Classify on the rounded percent so it matches the validator.
    const percent = isBlank(raw) ? null : round4(raw);
    const outOfRange = percent != null && !(percent >= 0 && percent < 100);
    return (
      <div
        className={`alert ${outOfRange ? "alert-danger" : "alert-info"} mt-2`}
        data-testid="cashback-limits-min-eligible-rtp-example"
      >
        {floor == null ? (
          <p className="mb-0">
            {outOfRange
              ? t("cashback.limits.min-eligible-rtp-out-of-range", undefined, "Enter an RTP percentage below 100 (e.g. 96.5).")
              : t("cashback.limits.min-eligible-rtp-example-none", undefined, "No floor set — rakeback and RTP-based cashback use each game's actual RTP.")}
          </p>
        ) : (
          <>
            <p className="mb-1">
              {t(
                "cashback.limits.min-eligible-rtp-example-intro",
                { floorPercent: formatPercent(floor), edgePercent: formatPercent(1 - floor) },
                "For a €100 wager at 10% rakeback, with the floor at {{floorPercent}}% RTP (house edge capped at {{edgePercent}}%):",
              )}
            </p>
            <ul className="mb-0">
              {EXAMPLE_SAMPLE_RTPS.map((rtp) => (
                <li key={rtp}>
                  {t(
                    "cashback.limits.min-eligible-rtp-example-row",
                    {
                      rtpPercent: formatPercent(rtp),
                      withoutFloor: formatEuro(examplePayout(rtp)),
                      withFloor: formatEuro(examplePayoutWithFloor(rtp, floor)),
                    },
                    "{{rtpPercent}}% RTP: €{{withoutFloor}} without floor, €{{withFloor}} with the floor",
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    );
  };

  const renderLimitSection = (type: CashbackTypeV2, index: number): JSX.Element => (
    <div
      className="card-box"
      key={type}
    >
      <div className="ibox-content">
        <h4 className="ibox-title">{typeLabel(type)}</h4>
        <div className="hr-line-dashed"></div>

        <FormGroup className="form-group">
          <div className="col-sm-12">
            <FormInput
              model={`limits[${index}].enabled`}
              type="checkbox"
              id={`cashback-limits-${type}-enabled`}
              data-testid={`cashback-limits-${type}-enabled`}
            />{" "}
            <label htmlFor={`cashback-limits-${type}-enabled`}>{t("cashback.limits.enabled-label")}</label>
            <span className="help d-block mt-1">{t("cashback.limits.enabled-help")}</span>
          </div>
        </FormGroup>

        <FormGroup className="form-group">
          <label className="col-sm-3 control-label">{t("cashback.limits.min-label")}</label>
          <div className="col-sm-10">
            <FormInput
              model={`limits[${index}].min`}
              validators={[Validators.number(), nonNegativeWhenSet()]}
              type="float"
              className="form-control"
              data-testid={`cashback-limits-${type}-min`}
            />
            <span className="help">{t("cashback.limits.min-help")}</span>
          </div>
        </FormGroup>

        <FormGroup className="form-group">
          <label className="col-sm-3 control-label">{t("cashback.limits.max-label")}</label>
          <div className="col-sm-10">
            <FormInput
              model={`limits[${index}].max`}
              validators={[Validators.number(), nonNegativeWhenSet(), maxAtLeastMin(index)]}
              type="float"
              className="form-control"
              data-testid={`cashback-limits-${type}-max`}
            />
            <span className="help">{t("cashback.limits.max-help")}</span>
          </div>
        </FormGroup>
      </div>
    </div>
  );

  return (
    <RouterViewTemplate
      status={status}
      title={t("cashback.limits.title")}
    >
      <div className="wrapper wrapper-content animated fadeInRight">
        <Form
          className="form form-horizontal"
          validationMap={form.validationMap}
          modelObject={form.model}
          formListener={formListener}
        >
          <div className="row">
            <div className="col-lg-12">
              <div className="alert alert-info">{t("cashback.limits.legacy-note")}</div>
              {renderGlobalSettings()}
              {LIMIT_TYPES.map((type, index) => renderLimitSection(type, index))}
            </div>
          </div>

          <div className="row">
            <div className="col-lg-12">
              <div className="card-box">
                <FormSubmitError />
                <FormSubmitSuccess text={t("cashback.limits.save-success")} />
                <div className="form-group">
                  <div className="col-sm-12">
                    <Submit
                      className="btn btn-primary"
                      data-testid="cashback-limits-save"
                    >
                      {t("cashback.limits.save")}
                    </Submit>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Form>
      </div>
    </RouterViewTemplate>
  );
};

export default EditCashbackLimits;
